import _ from 'lodash'
import moment from 'moment-timezone'
// Utils
import shuffle from './shuffle'
import slugify from './slugify'
// Types
import type {
  SubCategory,
  InterstitialAd,
  QuestionForDisplay,
  AdForDisplay,
  SeededDestination,
  BallotfulGroup,
} from '../types/ballot'

export type {QuestionForDisplay, AdForDisplay, SeededDestination, BallotfulGroup}

export const makeQuestionsForDisplay = (
  rawQuestions: SubCategory[] | null | undefined, groupSlug: string | null,
): QuestionForDisplay[] | undefined => {
  if (_.isEmpty(rawQuestions)) return

  const nonEmptyQuestions = (rawQuestions || []).filter(Boolean)
  const questionsWithNames = nonEmptyQuestions.filter(question => _.trim(question?.name))
  return _.map(questionsWithNames, (question: SubCategory) => {
    return {
      ...question,
      type: 'question' as const,
      slug: slugify(question.name),
      groupSlug: groupSlug,
    }
  })
}
const makeAdsForDisplay = (
  rawAds: InterstitialAd[] | null | undefined, groupSlug: string | null,
): AdForDisplay[] | undefined => {
  if (_.isEmpty(rawAds)) return

  const ads = (rawAds || []).filter(Boolean).filter((ad: InterstitialAd) => (ad?.mobile_image_path && ad?.image_path))
  return _.map(ads, (ad: InterstitialAd) => {
    return {
      ...ad,
      type: 'ad' as const,
      slug: `--ad-${ad?._id}`,
      groupSlug: groupSlug,
    }
  })
}

// TODO: Remove the need for the ballot in the group of this. Consider filtering the not-started ads out afterwards
export const seedInterstitialAdsByBallotfulGroup = (
  group: BallotfulGroup | null | undefined,
): SeededDestination[] | QuestionForDisplay[] | undefined => {
  if (!group || !group.ballot) return
  const publishSettings = group.ballot?.publish_settings
  const currentTimezone = moment.tz.names().find(name => name === publishSettings?.timezone) || moment.tz.guess()
  const currentDateTime = moment.tz(moment(), currentTimezone)
  // TODO: Should we just do this in the asyncBallot getting?
  const allQuestions = group.sub_categories || null
  const questions = makeQuestionsForDisplay(allQuestions, slugify(group.parent_category_name))
  const sortedQuestions = (group.sort_order === 'manual') ? questions : questions?.sort((a, b) => a?.name?.trim()?.localeCompare(b?.name?.trim()))

  const allInterstitialAds = group.interstitial_ads || null
  const currentInterstitialAds = _.filter(allInterstitialAds, interstitialAd => {
    const adStartDate = interstitialAd?.start_date && moment.tz(interstitialAd.start_date, currentTimezone)
    const adHasStarted = adStartDate ? adStartDate.isSameOrBefore(currentDateTime) : true
    return (Boolean(interstitialAd) && adHasStarted)
  })
  const interstitialAds = makeAdsForDisplay(currentInterstitialAds, slugify(group.parent_category_name))
  const shuffledInterstitialAds = shuffle(interstitialAds)

  if (sortedQuestions?.length && shuffledInterstitialAds?.length) {
    let currentAdIndex = 0
    let sortedQuestionsWithAds: SeededDestination[] = []

    _.forEach(sortedQuestions, (sortedQuestion, index) => {
      if ((index + 1) % 4 === 0) {
        const ad = shuffledInterstitialAds[currentAdIndex]
        sortedQuestionsWithAds.push({...ad, type: 'ad' as const})
        if ((currentAdIndex + 1) === shuffledInterstitialAds?.length)
          currentAdIndex = 0
        else
          currentAdIndex++
      }
      sortedQuestionsWithAds.push(sortedQuestion)
    })
    return sortedQuestionsWithAds
  } else {
    return questions
  }
}
